import { Button, Checkbox, Collapse, Spin, Typography, Divider, Card } from "@douyinfe/semi-ui";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiClient } from "../../api/ApiClient";
import { DashboardFilters, DashboardPref, HomeDashboardApi } from "./api/homeDashboard";
import { DashboardRefreshContext } from "./hooks/useDashboardModule";
import { dashboardLoadingMaskStyle } from "./styles";
import AiAnalysisCard from "./components/AiAnalysisCard";
import ConversionFunnelCard from "./components/ConversionFunnelCard";
import CustomerAssetsCard from "./components/CustomerAssetsCard";
import CustomerCallCard from "./components/CustomerCallCard";
import DetailDrawer, { DetailDrawerProps, DrawerMode } from "./components/DetailDrawer";
import FilterBar from "./components/FilterBar";
import LeaderboardCard from "./components/LeaderboardCard";
import VisitStarCard from "./components/VisitStarCard";

interface CurrentUser {
  id: number;
  name?: string;
  role?: string;
  teamId?: number;
}

const MODULE_LABELS: Record<string, string> = {
  assets: "客户结构", call: "客户通话", visit: "上门星级",
  funnel: "转化漏斗", leaderboard: "排行榜", ai: "AI 分析",
};

const DEFAULT_PREFS: DashboardPref[] = Object.keys(MODULE_LABELS).map((moduleCode, i) => ({
  moduleCode, visible: true, autoExpand: true, sortOrder: i,
}));

const DEFAULT_FILTERS: DashboardFilters = {
  startDate: dayjs().startOf("week").format("YYYY-MM-DD"),
  endDate: dayjs().format("YYYY-MM-DD"),
};

const api = ApiClient.getDefault();

export default function HomeDashboardPage() {
  const [prefs, setPrefs] = useState<DashboardPref[]>(DEFAULT_PREFS);
  const [prefsLoading, setPrefsLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState<CurrentUser | undefined>(undefined);
  const [draftFilters, setDraftFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<DashboardFilters>(DEFAULT_FILTERS);
  const [drawer, setDrawer] = useState<DetailDrawerProps | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [refreshNonce, setRefreshNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      HomeDashboardApi.getPrefs().catch(() => DEFAULT_PREFS),
      api.request<CurrentUser>("/auth/user", "GET").then(r => r.data).catch(() => undefined),
    ]).then(([saved, user]) => {
      if (cancelled) return;
      setPrefs(saved && saved.length ? saved : DEFAULT_PREFS);
      setCurrentUser(user);
      setPrefsLoading(false);
    });
    return () => { cancelled = true; };
  }, []);

  const isAdmin = !!currentUser && ["root", "op_manager", "monitor"].includes(currentUser.role || "");

  const openDrawer = useCallback(
    (title: string, params: Record<string, any>, mode: DrawerMode = "customer") => {
      setDrawer({ visible: true, title, mode, params, onClose: () => setDrawer(null) });
    },
    []
  );

  const prefLabel = (code: string) => MODULE_LABELS[code] || code;

  const visiblePrefs = [...prefs]
    .filter(p => p.visible)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const defaultActiveKey = visiblePrefs.filter(p => p.autoExpand).map(p => p.moduleCode);

  const updatePref = (moduleCode: string, field: "visible" | "autoExpand", value: boolean) => {
    const prev = prefs;
    const next = prev.map(p =>
      p.moduleCode === moduleCode ? { ...p, [field]: value } : p
    );
    setPrefs(next);
    HomeDashboardApi.updatePrefs(next)
      .then(saved => setPrefs(saved || next))
      .catch(() => setPrefs(prev));
  };

  const resetPrefs = () => {
    const prev = prefs;
    const next = Object.keys(MODULE_LABELS).map((moduleCode, i) => ({
      moduleCode, visible: true, autoExpand: true, sortOrder: i,
    }));
    setPrefs(next);
    HomeDashboardApi.updatePrefs(next)
      .then(saved => setPrefs(saved || next))
      .catch(() => setPrefs(prev));
  };

  const handleApply = () => {
    setQueryLoading(true);
    setAppliedFilters(draftFilters);
    setRefreshNonce((n) => n + 1);
    setTimeout(() => setQueryLoading(false), 600);
  };
  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const commonProps = useMemo(
    () => ({ filters: appliedFilters, isAdmin, currentTeamId: currentUser?.teamId, openDrawer }),
    [appliedFilters, isAdmin, currentUser?.teamId, openDrawer]
  );

  const renderCard = (moduleCode: string) => {
    switch (moduleCode) {
      case "assets": return <CustomerAssetsCard {...commonProps} />;
      case "call": return <CustomerCallCard {...commonProps} />;
      case "visit": return <VisitStarCard {...commonProps} />;
      case "funnel": return <ConversionFunnelCard {...commonProps} />;
      case "leaderboard": return <LeaderboardCard {...commonProps} />;
      case "ai": return <AiAnalysisCard {...commonProps} />;
      default: return null;
    }
  };

  if (prefsLoading) {
    return (
      <div style={dashboardLoadingMaskStyle}>
        <Spin style={{width:120}} size="large" tip="加载看板配置…" />
      </div>
    );
  }

  return (
    <div style={{ padding: 16, position: "relative" }}>
      {queryLoading && (
        <div style={dashboardLoadingMaskStyle}>
          <Spin style={{width:100}} size="large" tip="正在查询…" />
        </div>
      )}
      {/* <Typography.Title heading={4} style={{ marginBottom: 12 }}>首页看板</Typography.Title> */}

    <Card style={{marginBottom:8}}>
      <ModulePrefBar
        prefs={prefs}
        onChange={updatePref}
        onReset={resetPrefs}
      />

      <FilterBar
        value={draftFilters}
        onChange={setDraftFilters}
        onApply={handleApply}
        onReset={handleResetFilters}
        isAdmin={isAdmin}
        currentTeamId={currentUser?.teamId}
      />
    </Card>
      <style>{`
        .home-dashboard-collapse > .semi-collapse-item {
          border: 1px solid var(--semi-color-border);
          border-radius: 8px;
          overflow: hidden;
          margin-bottom: 10px;
        }
        .home-dashboard-collapse .semi-collapse-item-header {
          background: linear-gradient(90deg, var(--semi-color-primary-light-default), var(--semi-color-bg-1) 70%);
          padding: 10px 16px;
          font-weight: 600;
          transition: background .15s ease;
        }
        .home-dashboard-collapse .semi-collapse-item-header:hover {
          background: var(--semi-color-primary-light-active);
        }
        .home-dashboard-collapse .semi-collapse-item-active > .semi-collapse-item-header {
          background: var(--semi-color-primary-light-active);
          border-bottom: 1px solid var(--semi-color-border);
        }
        .home-dashboard-collapse .semi-collapse-item-header-text {
          font-size: 14px;
          color: var(--semi-color-text-0);
        }
        .home-dashboard-collapse .semi-collapse-item-content {
          padding: 12px;
          background: var(--semi-color-bg-0);
        }
      `}</style>
      <Collapse className="home-dashboard-collapse" keepDOM={false} defaultActiveKey={defaultActiveKey} accordion={false}>
        <DashboardRefreshContext.Provider value={refreshNonce}>
          {visiblePrefs.map(p => (
            <Collapse.Panel header={prefLabel(p.moduleCode)} itemKey={p.moduleCode} key={p.moduleCode}>
              {renderCard(p.moduleCode)}
            </Collapse.Panel>
          ))}
        </DashboardRefreshContext.Provider>
      </Collapse>
      {drawer && <DetailDrawer {...drawer} />}
    </div>
  );
}

function ModulePrefBar({
  prefs, onChange, onReset,
}: {
  prefs: DashboardPref[];
  onChange: (moduleCode: string, field: "visible" | "autoExpand", value: boolean) => void;
  onReset: () => void;
}) {
  const sorted = [...prefs].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 6,
      padding: "6px 6px", marginBottom: 6,
      background: "var(--semi-color-bg-1)", borderRadius: 8, flexWrap: "wrap",
    }}>
      <Typography.Text strong style={{ color: "var(--semi-color-text-2)", fontSize: 13 }}>模块设置</Typography.Text>
      {sorted.map(p => (
        <div key={p.moduleCode} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Checkbox checked={p.visible} onChange={(e) => onChange(p.moduleCode, "visible", e.target.checked)}>
            <span style={{ fontSize: 13 }}>{MODULE_LABELS[p.moduleCode] || p.moduleCode}</span>
          </Checkbox>
          <Checkbox checked={p.autoExpand} onChange={(e) => onChange(p.moduleCode, "autoExpand", e.target.checked)}
            aria-label={`${MODULE_LABELS[p.moduleCode]} 自动展开`}>
            <span style={{ fontSize: 12, color: "var(--semi-color-text-2)" }}>自动展开</span>
          </Checkbox>
          <Divider layout="vertical" margin='6px'/>
        </div>
        
      ))}
      <div style={{ marginLeft: "auto" }}>
        <Button size="small" theme="borderless" onClick={onReset}>恢复默认</Button>
      </div>
    </div>
  );
}
